import { Health } from './health';
import { Punch } from './punch';
import type { FightRules } from './rules';
import { Signal } from './signal';
import type { FightCommand, Hand, PunchAction, PunchStats } from './types';
import { isPunch } from './types';

export type FighterState = 'ready' | 'punching' | 'blocking' | 'dodging' | 'staggered' | 'knockedOut';

const moveKey = (hand: Hand, action: PunchAction) => `${hand}:${action}`;

/**
 * One boxer: his health, his stamina and the one thing he is doing. The bout decides what his punches do to the other.
 * A clean hit cuts off a punch he is still winding up and keeps him from punching for a moment; a staggering one stops
 * him altogether, but not again straight after.
 */
export class Fighter {
  readonly health: Health;
  readonly punchLanded = new Signal<PunchStats>();
  /** His punch was cut off before it landed. */
  readonly interrupted = new Signal<PunchStats>();

  state: FighterState = 'ready';
  /** The punch he is throwing; null unless he is punching. */
  currentPunch: Punch | null = null;
  /** Seconds since the current state began. */
  stateSeconds = 0;
  stamina: number;

  private readonly punches = new Map<string, Punch>();
  private secondsLeft = 0;
  private stunLeft = 0;
  private staggerGuardLeft = 0;
  private staminaDelayLeft = 0;
  private hitsTowardsSuper = 0;

  constructor(maxHealth: number, private readonly rules: FightRules, moves: Iterable<PunchStats>) {
    this.health = new Health(maxHealth);
    this.stamina = rules.staminaMax;
    for (const stats of moves) {
      const punch: Punch = new Punch(stats, () => this.punchLanded.emit(punch.stats));
      this.punches.set(moveKey(stats.hand, stats.action), punch);
    }
  }

  /** In the dodge itself, not its recovery: a punch to the head goes past. */
  get isDodging(): boolean {
    return this.state === 'dodging' && this.stateSeconds < this.rules.dodgeSeconds;
  }

  /** Reeling from a clean hit: he cannot start a punch, though he can block and dodge. */
  get isStunned(): boolean {
    return this.stunLeft > 0;
  }

  /** In his guard and not reeling: he could start a punch he has the stamina for. */
  get canPunch(): boolean {
    return this.state === 'ready' && !this.isStunned;
  }

  get staminaFraction(): number {
    return this.stamina / this.rules.staminaMax;
  }

  /** How charged his super punch is, 0..1; he can throw it only at 1. */
  get superCharge(): number {
    return this.hitsTowardsSuper / this.rules.superHits;
  }

  /** Whether this punch is one of his, whatever he is doing now. */
  knows(hand: Hand, action: PunchAction): boolean {
    return this.punches.has(moveKey(hand, action));
  }

  /** He could start this punch now but for his stamina. */
  tooTiredFor(hand: Hand, action: PunchAction): boolean {
    const punch = this.punches.get(moveKey(hand, action));
    return this.canPunch && punch !== undefined && punch.stats.staminaCost > this.stamina;
  }

  /** The numbers of one of his punches, or undefined. */
  move(hand: Hand, action: PunchAction): PunchStats | undefined {
    return this.punches.get(moveKey(hand, action))?.stats;
  }

  /** True when he does it; a command he cannot follow now changes nothing. */
  submit(command: FightCommand): boolean {
    switch (command.action) {
      case 'blockStart':
        if (this.state !== 'ready') return false;
        this.enter('blocking');
        return true;
      case 'blockEnd':
        if (this.state !== 'blocking') return false;
        this.enter('ready');
        return true;
      case 'dodge':
        if (this.state !== 'ready') return false;
        this.enter('dodging');
        this.secondsLeft = this.rules.dodgeSeconds + this.rules.dodgeRecoverySeconds;
        return true;
    }
    if (!isPunch(command.action) || !this.canPunch) return false;
    const punch = this.punches.get(moveKey(command.hand, command.action));
    if (!punch || punch.stats.staminaCost > this.stamina) return false;
    const isSuper = command.action === 'super';
    if (isSuper && this.hitsTowardsSuper < this.rules.superHits) return false;
    if (!punch.tryStart()) return false;
    if (isSuper) this.hitsTowardsSuper = 0;
    this.stamina -= punch.stats.staminaCost;
    this.staminaDelayLeft = this.rules.staminaDelaySeconds;
    this.enter('punching');
    this.currentPunch = punch;
    return true;
  }

  tick(seconds: number): void {
    this.stateSeconds += seconds;
    this.stunLeft = Math.max(0, this.stunLeft - seconds);
    this.staggerGuardLeft = Math.max(0, this.staggerGuardLeft - seconds);
    this.recoverStamina(seconds);
    switch (this.state) {
      case 'punching':
        this.currentPunch!.tick(seconds);
        if (this.currentPunch!.phase === 'idle') this.backToReady();
        break;
      case 'dodging':
      case 'staggered':
        this.secondsLeft -= seconds;
        if (this.secondsLeft <= 0) this.backToReady();
        break;
    }
  }

  /**
   * Takes a punch. A clean one cuts off a punch he is still winding up and stuns him for a moment; a clean staggering
   * one stops him altogether, unless he was staggered only just now.
   */
  takeHit(damage: number, clean: boolean, staggers: boolean): void {
    if (this.state === 'knockedOut') return;
    this.health.take(damage);
    if (this.health.isEmpty) {
      this.cancelPunch();
      this.enter('knockedOut');
      return;
    }
    if (!clean) return;
    if (this.currentPunch?.phase === 'windup') {
      const cut = this.currentPunch.stats;
      this.cancelPunch();
      this.enter('ready');
      this.interrupted.emit(cut);
    }
    if (staggers && this.staggerGuardLeft <= 0) {
      this.cancelPunch();
      this.enter('staggered');
      this.secondsLeft = this.rules.staggerSeconds;
      this.staggerGuardLeft = this.rules.staggerGuardSeconds;
      return;
    }
    this.stunLeft = Math.max(this.stunLeft, this.rules.hitStunSeconds);
  }

  /** One of his punches landed clean: a step towards his super punch. */
  landedClean(): void {
    this.hitsTowardsSuper = Math.min(this.hitsTowardsSuper + 1, this.rules.superHits);
  }

  /** The rest between rounds: back in his guard, with a share of the health he lost and all his stamina. */
  /** The bell: whatever he was throwing is dropped and he stands in his guard, unless he is down. */
  standDown(): void {
    if (this.state === 'knockedOut') return;
    this.cancelPunch();
    this.enter('ready');
    this.stunLeft = 0;
  }

  rest(recoveryShare: number): void {
    if (this.state === 'knockedOut') return;
    this.standDown();
    this.stamina = this.rules.staminaMax;
    this.health.heal(Math.floor((this.health.max - this.health.current) * recoveryShare));
  }

  private recoverStamina(seconds: number): void {
    if (this.state === 'punching' || this.state === 'knockedOut') return;
    if (this.staminaDelayLeft > 0) {
      this.staminaDelayLeft -= seconds;
      return;
    }
    const rate = this.rules.staminaPerSecond * (this.state === 'blocking' ? this.rules.staminaWhileBlocking : 1);
    this.stamina = Math.min(this.rules.staminaMax, this.stamina + rate * seconds);
  }

  private cancelPunch(): void {
    this.currentPunch?.cancel();
    this.currentPunch = null;
  }

  private backToReady(): void {
    this.currentPunch = null;
    this.enter('ready');
  }

  private enter(state: FighterState): void {
    this.state = state;
    this.stateSeconds = 0;
  }
}
